import { useState, useEffect, useCallback, useRef } from 'react';
import { Tabs, List, Button, Space, message, Tag, Typography, Badge, Empty, Modal, Select, Alert, Form, Input, Spin, Tooltip } from 'antd';
import { LinkOutlined, SearchOutlined, PlusOutlined, ShareAltOutlined, CopyOutlined, DeleteOutlined, PushpinFilled, PushpinOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import ConnectionEditModal from './ConnectionEditModal';
import CreateEntityForm, { windowFields } from './CreateEntityForm';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import InstallAppModal from '../Apps/InstallAppModal';
import { SplitModal, MergeModal } from './SplitMerge';
import './ConnectedEntities.css';

const truncateToSentences = (text, max = 2) => {
    if (!text) return undefined;
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
    if (sentences.length <= max) return text.trim();
    return sentences.slice(0, max).join(' ').trim() + ' …';
};

const TABS = [
    { key: 'context',      label: 'Contexts' },
    { key: 'relationship', label: 'Relationships' },
    { key: 'person',       label: 'People' },
    { key: 'app',          label: 'Apps' },
];

function ConnectedEntities({ connectedEntities, isAdmin, entityType, currentEntityId, entityName, onConnectionUpdated, inviteToken, onInviteUpdated }) {
    const { user, userPersonEntity } = useAuth();
    const navigate = useNavigate();
    const api = useApi();
    // Connecting/searching needs a context or relationship hub to exist at all
    // (see backend assert_connectable_types) — hidden entirely when this node's
    // tier has neither type enabled (e.g. Tier 1/2, person-only).
    const { connectionsEnabled } = useNodeInfo();
    const [acceptingId, setAcceptingId] = useState(null);
    const [denyingId, setDenyingId] = useState(null);
    const [selectedConn, setSelectedConn] = useState(null);

    const [connectModalOpen, setConnectModalOpen] = useState(false);
    const [installOpen, setInstallOpen] = useState(false);
    const [splitOpen, setSplitOpen] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [adminEntities, setAdminEntities] = useState([]);
    const [adminEntitiesLoading, setAdminEntitiesLoading] = useState(false);
    const [connectSearchValue, setConnectSearchValue] = useState('');
    const [connectSelectedId, setConnectSelectedId] = useState(null);
    const [connecting, setConnecting] = useState(false);

    const [createForm] = Form.useForm();
    const [creating, setCreating] = useState(false);

    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [sharingBusy, setSharingBusy] = useState(false);

    const [pinOverrides, setPinOverrides] = useState({});
    const [pinningId, setPinningId] = useState(null);
    const userId = user?.id;

    const isPinnedForCurrentEntity = (conn) => {
        const override = pinOverrides[conn.connection_id];
        if (conn.entity_a_id === currentEntityId) {
            return override ? !!override.pinned_on_entity_a : !!conn.pinned_on_entity_a;
        }
        if (conn.entity_b_id === currentEntityId) {
            return override ? !!override.pinned_on_entity_b : !!conn.pinned_on_entity_b;
        }
        return false;
    };

    const togglePin = async (e, conn) => {
        e.stopPropagation();
        if (!userId || !conn?.connection_id) return;
        const isASide = conn.entity_a_id === currentEntityId;
        const field = isASide ? 'pinned_on_entity_a' : 'pinned_on_entity_b';
        const nextValue = !isPinnedForCurrentEntity(conn);

        setPinOverrides(prev => ({
            ...prev,
            [conn.connection_id]: { ...(prev[conn.connection_id] || {}), [field]: nextValue },
        }));
        setPinningId(conn.connection_id);

        try {
            await api.put(`/api/connections/${conn.connection_id}`, { [field]: nextValue }, { params: { user_id: userId } });
            onConnectionUpdated?.();
            setPinOverrides(prev => {
                const next = { ...prev };
                delete next[conn.connection_id];
                return next;
            });
        } catch (err) {
            setPinOverrides(prev => {
                const next = { ...prev };
                delete next[conn.connection_id];
                return next;
            });
            message.error(err.message || 'Failed to update pin');
        } finally {
            setPinningId(null);
        }
    };

    const tabsRef = useRef(null);
    const [tabsMinHeight, setTabsMinHeight] = useState(0);
    useEffect(() => {
        const el = tabsRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setTabsMinHeight(prev => Math.max(prev, entry.contentRect.height));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        if (!isAdmin || !user?.id || !connectionsEnabled) return;
        setAdminEntitiesLoading(true);
        api.get('/api/entities', { params: { entity_type: 'person', user_id: user.id } })
            .then(async (personEntities) => {
                const personEntity = personEntities[0];
                if (!personEntity) return [];
                const connected = await api.get(`/api/entities/${personEntity.id}/connected`).catch(() => []);
                const candidates = connected.filter(c => c.connected_status && c.entity.id !== currentEntityId);
                const adminChecks = await Promise.all(
                    candidates.map(c =>
                        api.get(`/api/entities/${c.entity.id}/admin-check`, { params: { user_id: user.id } })
                            .then(d => d.is_admin ? c.entity : null)
                            .catch(() => null)
                    )
                );
                return adminChecks.filter(Boolean);
            })
            .then(entities => setAdminEntities(entities))
            .catch(() => {})
            .finally(() => setAdminEntitiesLoading(false));
    }, [isAdmin, user?.id, currentEntityId, connectionsEnabled]);

    const handleCreateAndConnect = async (values) => {
        setCreating(true);
        let newEntity = null;
        try {
            const descriptions = (values.descriptions || []).filter(d => d?.content?.trim());
            const defaultDesc = descriptions[0];

            newEntity = await api.post('/api/entities', {
                name: values.name,
                entity_type: values.entity_type,
                url: values.url || null,
                email: values.email || null,
                attributes: values.attributes ? JSON.parse(values.attributes) : {},
                description: defaultDesc?.content || null,
                ...windowFields(values.when),
                ...(userPersonEntity?.id ? { created_by: userPersonEntity.id } : {}),
            });

            // Extra descriptions are best-effort; a failure here must not roll
            // back the entity (only the connection below does).
            for (let i = 1; i < descriptions.length; i++) {
                const d = descriptions[i];
                await api.post(`/api/entities/${newEntity.id}/descriptions`,
                    { entity_id: newEntity.id, label: d.label || 'default', content: d.content, is_default: false }
                ).catch(() => {});
            }

            // The backend bonds a new context/relationship to its creator as
            // admin, so there is no creator link to add here — and when this
            // page is the creator's own person entity, that bond already is
            // the connection asked for.
            const creatorBonded = ['context', 'relationship'].includes(newEntity.entity_type)
                && newEntity.created_by != null && newEntity.created_by === currentEntityId;
            if (!creatorBonded) {
                const a = Math.min(currentEntityId, newEntity.id);
                const b = Math.max(currentEntityId, newEntity.id);
                await api.post('/api/connections', { entity_a_id: a, entity_b_id: b, connected_status: true });
            }

            message.success(`"${newEntity.name}" created and connected`);
            createForm.resetFields();
            onConnectionUpdated?.();
            setConnectModalOpen(false);
        } catch (err) {
            if (newEntity?.id) {
                await api.del(`/api/entities/${newEntity.id}`, { params: { user_id: userId } }).catch(() => {});
            }
            message.error(err.message || 'Failed to create and connect entity');
        } finally {
            setCreating(false);
        }
    };

    const handleConnect = async () => {
        if (!connectSelectedId || !user?.id) return;
        setConnecting(true);
        try {
            const a = Math.min(currentEntityId, connectSelectedId);
            const b = Math.max(currentEntityId, connectSelectedId);
            await api.post('/api/connections', { entity_a_id: a, entity_b_id: b, connected_status: true });
            message.success('Entities connected');
            setConnectSelectedId(null);
            setConnectSearchValue('');
            onConnectionUpdated?.();
            setConnectModalOpen(false);
        } catch (err) {
            message.error(err.message || 'Failed to connect entities');
        } finally {
            setConnecting(false);
        }
    };

    const performSearch = async (query) => {
        if (!query || query.trim().length < 2) { setSearchResults([]); return; }
        setSearching(true);
        try {
            const results = await api.post('/api/entities/search',
                { query, limit: 20, vector_weight: 0.5, text_weight: 0.5, entity_id: currentEntityId }
            );
            setSearchResults(results);
            setSearchModalOpen(true);
        } catch (error) {
            message.error('Search failed: ' + error.message);
        } finally {
            setSearching(false);
        }
    };

    const debouncedSearch = useCallback(debounce((query) => performSearch(query), 500), []);

    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedSearch(query);
    };

    const handleAccept = async (e, connectionId) => {
        e.stopPropagation();
        setAcceptingId(connectionId);
        try {
            await api.put(`/api/connections/${connectionId}`, { connected_status: true }, { params: { user_id: userId } });
            onConnectionUpdated?.();
        } catch (error) {
            message.error(error.message || 'Failed to accept connection');
        } finally {
            setAcceptingId(null);
        }
    };

    const handleDeny = async (e, connectionId) => {
        e.stopPropagation();
        setDenyingId(connectionId);
        try {
            await api.del(`/api/connections/${connectionId}`, { params: { user_id: userId } });
            onConnectionUpdated?.();
        } catch (error) {
            console.error('Error denying connection:', error);
        } finally {
            setDenyingId(null);
        }
    };

    const handleGenerateInvite = async () => {
        setSharingBusy(true);
        try {
            await api.post(`/api/entities/${currentEntityId}/invite-link`, undefined, { params: { user_id: userId } });
            onInviteUpdated?.();
        } catch {
            message.error('Failed to generate link');
        } finally {
            setSharingBusy(false);
        }
    };

    const handleRevokeInvite = async () => {
        setSharingBusy(true);
        try {
            await api.del(`/api/entities/${currentEntityId}/invite-link`, { params: { user_id: userId } });
            onInviteUpdated?.();
        } catch {
            message.error('Failed to revoke link');
        } finally {
            setSharingBusy(false);
        }
    };

    const canShowActionButtons = isAdmin && (entityType === 'context' || entityType === 'relationship');

    const filteredConnectOptions = adminEntities
        .filter(e => !connectSearchValue || e.name.toLowerCase().includes(connectSearchValue.toLowerCase()))
        .map(e => ({ value: e.id, label: `${e.name} [${e.entity_type}]` }));

    const handleModalClose = () => {
        setConnectSelectedId(null);
        setConnectSearchValue('');
        createForm.resetFields();
        setConnectModalOpen(false);
    };

    const renderItem = (conn) => {
        const pinned = isPinnedForCurrentEntity(conn);
        const actions = [];
        if (canShowActionButtons && !conn.connected_status) {
            actions.push(
                <Button key="accept" size="small" type="primary" loading={acceptingId === conn.connection_id} onClick={e => handleAccept(e, conn.connection_id)}>Accept</Button>,
                <Button key="deny" size="small" danger loading={denyingId === conn.connection_id} onClick={e => handleDeny(e, conn.connection_id)}>Deny</Button>
            );
        }
        if (isAdmin) {
            if (conn.connected_status) {
                actions.push(
                    <Tooltip key="pin" title={pinned ? 'Unpin' : 'Pin to top'}>
                        <Button
                            type="text"
                            size="small"
                            loading={pinningId === conn.connection_id}
                            icon={pinned ? <PushpinFilled style={{ color: '#1890ff' }} /> : <PushpinOutlined />}
                            onClick={e => togglePin(e, conn)}
                        />
                    </Tooltip>
                );
            }
            actions.push(
                <Button key="edit" type="text" size="small" onClick={() => setSelectedConn(conn)}>Edit</Button>
            );
        }

        return (
            <List.Item key={conn.connection_id} actions={actions}>
                <List.Item.Meta
                    title={
                        <Space size={6}>
                            {pinned && (
                                <Tooltip title="Pinned by an admin">
                                    <PushpinFilled style={{ color: '#1890ff', fontSize: 11 }} />
                                </Tooltip>
                            )}
                            <Typography.Text
                                strong
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(conn.entity.entity_type === 'app' ? `/app/${conn.entity.id}` : `/entity/${conn.entity.id}`)}
                            >
                                {conn.entity.name}
                            </Typography.Text>
                            {!conn.connected_status && <Tag color="warning" style={{ fontSize: 11 }}>Pending</Tag>}
                            {conn.state === 'paused' && <Tag color="purple" style={{ fontSize: 11 }}>Paused</Tag>}
                            {conn.state === 'before' && <Tag color="blue" style={{ fontSize: 11 }}>Not started</Tag>}
                            {conn.state === 'after' && <Tag style={{ fontSize: 11 }}>Ended</Tag>}
                            {conn.thickness != null && conn.thickness > 0 && (
                                <Tooltip title="How much this connection is used lately">
                                    <span aria-label={`used ${['', 'a little', 'often', 'a lot'][conn.thickness]}`} style={{ color: 'var(--text-secondary)', letterSpacing: 1 }}>
                                        {'●'.repeat(conn.thickness)}
                                    </span>
                                </Tooltip>
                            )}
                            {conn.role === 'admin' && <Tag color="blue" style={{ fontSize: 11 }}>admin</Tag>}
                        </Space>
                    }
                    description={
                        <Space direction="vertical" size={2}>
                            {truncateToSentences(conn.entity.description)}
                            {conn.entity.url && (
                                <Typography.Link
                                    href={conn.entity.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    style={{ fontSize: 12 }}
                                >
                                    <LinkOutlined style={{ marginRight: 4 }} />
                                    {conn.entity.url}
                                </Typography.Link>
                            )}
                        </Space>
                    }
                />
            </List.Item>
        );
    };

    const defaultTab = TABS.find(t => connectedEntities.some(c => c.entity.entity_type === t.key && !c.entity.attributes?.is_dm))?.key ?? 'context';

    const getRestrictionNotice = (tabKey) => {
        if (entityType === 'relationship' && tabKey === 'relationship') {
            return 'Relationship entities cannot be connected to other relationship entities.';
        }
        if (entityType === 'person' && tabKey === 'person') {
            return 'Person entities cannot be connected to other person entities.';
        }
        if (entityType === 'app' && tabKey === 'app') {
            return 'An app is installed in other entities, never in another app.';
        }
        return null;
    };

    const tabItems = TABS.map(({ key, label }) => {
        const items = connectedEntities
            // DM relationship entities are messaging plumbing, not a real link —
            // they only ever surface through the Messages UI.
            .filter(c => c.entity.entity_type === key && !c.entity.attributes?.is_dm)
            .slice()
            .sort((a, b) => {
                const aP = isPinnedForCurrentEntity(a) ? 1 : 0;
                const bP = isPinnedForCurrentEntity(b) ? 1 : 0;
                return bP - aP;
            });
        const pendingCount = isAdmin ? items.filter(c => !c.connected_status).length : 0;
        const restrictionNotice = getRestrictionNotice(key);

        return {
            key,
            label: (
                <Space size={6} style={{ opacity: restrictionNotice ? 0.35 : 1 }}>
                    {label}
                    {items.length > 0 && (
                        <span style={{ fontSize: 11, color: pendingCount > 0 ? 'orange' : 'var(--text-secondary)' }}>
                            {items.length}
                        </span>
                    )}
                </Space>
            ),
            children: (
                <>
                    {restrictionNotice && (
                        <Alert
                            message={restrictionNotice}
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    {items.length === 0 ? (
                        <Empty
                            description={`No ${label.toLowerCase()} linked`}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ padding: '24px 0' }}
                        />
                    ) : (
                        <List dataSource={items} renderItem={renderItem} size="small" />
                    )}
                </>
            ),
        };
    });

    // A connection always needs a context or relationship hub at one end (backend
    // assert_connectable_types), so on a person-only node none exist and
    // none can be made — the whole block is an empty bordered box. Invite is the
    // one action here that isn't a connection, so a group entity left over on a
    // downgraded node still renders the toolbar for it.
    if (!connectionsEnabled && !canShowActionButtons) return null;

    return (
        <>
            {/* ── Invite link modal ── */}
            <Modal
                title={<><ShareAltOutlined style={{ marginRight: 8 }} />Invite link — {entityName}</>}
                open={shareModalOpen}
                onCancel={() => setShareModalOpen(false)}
                footer={null}
                width={500}
            >
                {inviteToken ? (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        <Input
                            readOnly
                            value={`${window.location.origin}/join/${inviteToken}`}
                            addonAfter={
                                <Button type="text" size="small" icon={<CopyOutlined />} style={{ padding: 0 }}
                                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join/${inviteToken}`); message.success('Link copied'); }}
                                />
                            }
                        />
                        <Button danger icon={<DeleteOutlined />} loading={sharingBusy} onClick={handleRevokeInvite}>
                            Revoke link
                        </Button>
                    </Space>
                ) : (
                    <Space direction="vertical" size={8}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                            No invite link yet. Generate one to let people discover and request to join this {entityType}.
                        </span>
                        <Button type="primary" icon={<ShareAltOutlined />} loading={sharingBusy} onClick={handleGenerateInvite}>
                            Generate invite link
                        </Button>
                    </Space>
                )}
            </Modal>

            {/* ── Search modal ── */}
            <Modal
                title="Search Entities"
                open={searchModalOpen}
                onCancel={() => { setSearchModalOpen(false); setSearchQuery(''); setSearchResults([]); }}
                footer={null}
                width={800}
            >
                <div style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Search entities..."
                        prefix={searching ? <Spin size="small" /> : <SearchOutlined />}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        allowClear
                        size="large"
                        autoFocus
                    />
                </div>
                {searching ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin /></div>
                ) : searchResults.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>No results found</div>
                ) : (
                    <List
                        dataSource={searchResults}
                        renderItem={(result) => (
                            <List.Item
                                key={result.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => { setSearchModalOpen(false); navigate(`/entity/${result.id}`); }}
                            >
                                <List.Item.Meta
                                    title={
                                        <Space>
                                            <strong>{result.name}</strong>
                                            <Tag color="blue">{result.entity_type}</Tag>
                                            <Badge count={`Score: ${result.score.toFixed(3)}`} style={{ backgroundColor: '#52c41a' }} />
                                        </Space>
                                    }
                                    description={
                                        <div>
                                            {result.description && <p style={{ marginBottom: 8 }}>{result.description}</p>}
                                            <Space size="small" style={{ fontSize: '0.85em', color: '#888' }}>
                                                {result.vector_score !== null && <span>Vector: {result.vector_score.toFixed(3)}</span>}
                                                {result.text_score !== null && <span>Text: {result.text_score.toFixed(3)}</span>}
                                            </Space>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                )}
            </Modal>

            {/* ── Connect modal ── */}
            <Modal title="Connect an entity" open={connectModalOpen} onCancel={handleModalClose} footer={null} width={560}>
                <Tabs
                    size="small"
                    style={{ marginTop: 4 }}
                    items={[
                        {
                            key: 'existing',
                            label: 'Connect Existing',
                            children: (
                                <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                                    <Select
                                        showSearch
                                        style={{ flex: 1 }}
                                        placeholder="Search your entities…"
                                        filterOption={false}
                                        searchValue={connectSearchValue}
                                        onSearch={setConnectSearchValue}
                                        loading={adminEntitiesLoading}
                                        options={filteredConnectOptions}
                                        value={connectSelectedId}
                                        onChange={setConnectSelectedId}
                                        allowClear
                                        onClear={() => { setConnectSelectedId(null); setConnectSearchValue(''); }}
                                        notFoundContent={adminEntitiesLoading ? 'Loading…' : 'No entities found'}
                                    />
                                    <Button type="primary" icon={<LinkOutlined />} loading={connecting} disabled={!connectSelectedId} onClick={handleConnect}>
                                        Connect
                                    </Button>
                                </Space.Compact>
                            ),
                        },
                        {
                            key: 'create',
                            label: 'Create & Connect',
                            children: (
                                <div style={{ marginTop: 4 }}>
                                    <CreateEntityForm
                                        form={createForm}
                                        onFinish={handleCreateAndConnect}
                                        loading={creating}
                                        submitLabel="Create & Connect"
                                    />
                                </div>
                            ),
                        },
                    ]}
                />
            </Modal>

            <div className="connected-entities">
                {isAdmin && (connectionsEnabled || canShowActionButtons) && (
                    <div className="connected-entities-toolbar">
                        <Space>
                            {connectionsEnabled && (
                                <>
                                    <Button size="small" icon={<SearchOutlined />} onClick={() => setSearchModalOpen(true)}>Search</Button>
                                    <Button size="small" icon={<PlusOutlined />} onClick={() => setConnectModalOpen(true)}>Connect</Button>
                                </>
                            )}
                            {canShowActionButtons && (
                                <Button size="small" icon={<ShareAltOutlined />} onClick={() => setShareModalOpen(true)}>Invite</Button>
                            )}
                            {entityType !== 'app' && (
                                <Button size="small" icon={<AppstoreAddOutlined />} onClick={() => setInstallOpen(true)}>Install app</Button>
                            )}
                            {canShowActionButtons && (
                                <>
                                    <Button size="small" onClick={() => setSplitOpen(true)}>Split</Button>
                                    <Button size="small" onClick={() => setMergeOpen(true)}>Merge</Button>
                                </>
                            )}
                        </Space>
                    </div>
                )}
                {connectionsEnabled && (
                    <div ref={tabsRef} className="connected-entities-tabs" style={{ minHeight: tabsMinHeight || undefined }}>
                        <Tabs defaultActiveKey={defaultTab} items={tabItems} size="small" />
                    </div>
                )}
            </div>

            {canShowActionButtons && (
                <>
                    <SplitModal open={splitOpen} onClose={() => setSplitOpen(false)}
                        entity={{ id: currentEntityId, name: entityName, entity_type: entityType }}
                        connections={connectedEntities} onDone={onConnectionUpdated} />
                    <MergeModal open={mergeOpen} onClose={() => setMergeOpen(false)}
                        entity={{ id: currentEntityId, name: entityName, entity_type: entityType }}
                        onDone={onConnectionUpdated} />
                </>
            )}

            <InstallAppModal
                open={installOpen}
                onClose={() => setInstallOpen(false)}
                hostId={currentEntityId}
                hostName={entityName}
                onInstalled={onConnectionUpdated}
            />

            <ConnectionEditModal
                open={!!selectedConn}
                onClose={() => setSelectedConn(null)}
                conn={selectedConn}
                currentEntityId={currentEntityId}
                isAdmin={isAdmin}
                canAcceptConnection={canShowActionButtons}
                userId={userId}
                onSuccess={() => { setSelectedConn(null); onConnectionUpdated?.(); }}
            />

        </>
    );
}

export default ConnectedEntities;
